import React from 'react';
import { motion } from 'framer-motion';
import { Trans } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconArrowRight, IconAt, IconCheck, IconMessageQuestion, IconVideo } from '@tabler/icons-react';
import Composer from '../../../../components/Composer';
import StudioAi from '../../../../components/StudioAi';
import InterestReel from '../InterestReel';
import Reasons from './components/Reasons';
import GrowthBadge from './components/GrowthBadge';
import Plans from './components/Plans';
import Faq from './components/Faq';
import useLanding from './hooks/useLanding';
import './styles.scss';

const PERKS = ['landingFree', 'landingNoCard', 'landingOnlyHandle'];
const STEPS = [{ step: 1, icon: <IconAt /> }, { step: 2, icon: <IconMessageQuestion /> }, { step: 3, icon: <IconVideo /> }];

// The title's niche, written by hand in the Studio's vivid colours (`<topic>` in the copy).
const Topic = ({ children }) => <InterestReel inline interests={[[].concat(children).join('')]} />;

// Before sign-in the home is a small landing page for one niche (`?mode=`): the
// promise and the @ that starts the onboarding, why not a generic AI (three animated
// reasons), the three steps to a script, the plans and the questions people ask.
// The @ is the only way in; every later call to action leads back to it.
export default function Landing({ studio: s }) {
  const { t } = s;
  const { scroller, locale, copy, reveal, start, choose } = useLanding(s);
  return <div ref={scroller} className="studio-landing">
    <div className="studio-landing__inner">
      <motion.section className="studio-landing__hero" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <StudioAi size={56} />
        <Text.h1 className="studio-landing__title" weight={500} contrast={10}>
          <Trans t={t} i18nKey="landingTitle" values={{ topic: copy.topic }} components={{ topic: <Topic /> }} />
        </Text.h1>
        <Text.p className="studio-landing__lead" contrast={7}>{t('landingSubtitle', copy)}</Text.p>
        <div className="studio-landing__start">
          <div className="studio-landing__start-heading">
            <Text.h5 weight={500} contrast={9}>{t('landingStartTitle')}</Text.h5>
            <Text.span contrast={6}>{t('landingStartText')}</Text.span>
          </div>
          <Composer studio={s} />
          <blockquote className="studio-landing__quote"><Text.p contrast={7}>{t('landingQuote')}</Text.p></blockquote>
          <ul className="studio-landing__perks">
            {PERKS.map(key => <li key={key}><Text.caption contrast={6} left={<IconCheck />}>{t(key)}</Text.caption></li>)}
          </ul>
        </div>
      </motion.section>

      <Reasons t={t} locale={locale} copy={copy} reveal={reveal} root={scroller} />

      <motion.section className="studio-landing__section" aria-labelledby="studio-landing-steps" {...reveal}>
        <header className="studio-landing__heading is-wide">
          <GrowthBadge />
          <Text.h2 id="studio-landing-steps" weight={500} contrast={10}>{t('landingStepsTitle', copy)}</Text.h2>
        </header>
        <ol className="studio-landing__steps">
          {STEPS.map(({ step, icon }) => <li key={step} className="studio-landing__step">
            <span className="studio-landing__step-icon" aria-hidden="true">{icon}</span>
            <Text.label>{t(`landingStep${step}Title`)}</Text.label>
            <Text.p contrast={6}>{t(`landingStep${step}Text`)}</Text.p>
          </li>)}
        </ol>
        <Button color="inverse" size="large" variant="rounded" endIcon={<IconArrowRight />} onClick={start}>{t('landingCta')}</Button>
      </motion.section>

      <Plans t={t} locale={locale} reveal={reveal} root={scroller} choose={choose} />

      <Faq t={t} copy={copy} reveal={reveal} />
    </div>
  </div>;
}
